import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getMyAds, deleteAd, getMemberUsage } from '../../../services/membersApi';
import AdFormModal from '../../advertising/AdFormModal';
import Modal from '../../common/Modal/Modal';
import type { Ad } from '../../../types';
import './MyAdsList.css';

interface MyAdsListProps {
  onUpdate?: () => void;
}

// Plan limits configuration
const PLAN_LIMITS: Record<string, { maxAds: number | null }> = {
  free: { maxAds: 0 },
  basic: { maxAds: 10 },
  premium: { maxAds: null },
};

const MyAdsList = ({ onUpdate }: MyAdsListProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdId, setEditingAdId] = useState<string | null>(null);

  // Usage and limit state
  const [usageStats, setUsageStats] = useState({ currentAds: 0, currentContacts: 0 });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitReason, setLimitReason] = useState('');

  // Status configuration
  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; icon: string; className: string; message?: string }> = {
      pending: {
        label: '待審核',
        icon: '⏳',
        className: 'pending',
        message: '等待管理員審核中，請耐心等候',
      },
      active: {
        label: '已發布',
        icon: '✅',
        className: 'active',
        message: '廣告已發布並公開顯示',
      },
      rejected: {
        label: '未通過',
        icon: '❌',
        className: 'rejected',
        message: '廣告未通過審核，請編輯後重新提交',
      },
      inactive: {
        label: '下架',
        icon: '📦',
        className: 'inactive',
        message: '廣告已下架，不再公開顯示',
      },
    };
    return configs[status] || { label: status, icon: '📋', className: 'unknown', message: '' };
  };

  const fetchAds = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getMyAds({ limit: 50 });
      setAds(result.data);
    } catch (err) {
      console.error('Failed to fetch ads:', err);
      setError('載入廣告失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = useCallback(async () => {
    try {
      const usage = await getMemberUsage();
      if (usage) {
        setUsageStats({
          currentAds: usage.currentAds,
          currentContacts: usage.currentContacts,
        });
        return usage;
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchAds();
    fetchUsageStats();
  }, [fetchUsageStats]);

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`確定要刪除「${title}」嗎？此操作無法復原。`)) {
      return;
    }

    setDeletingId(id);
    setError('');

    try {
      const success = await deleteAd(id);

      if (success) {
        // Remove from list
        setAds((prev) => prev.filter((ad) => ad.id !== id));
        if (onUpdate) {
          onUpdate();
        }
      } else {
        setError('刪除失敗，請稍後再試');
      }
    } catch (err) {
      console.error('Failed to delete ad:', err);
      setError('刪除失敗，請稍後再試');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (adId: string) => {
    setEditingAdId(adId);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingAdId(null);
  };

  const handleEditSuccess = async () => {
    // Refresh the ads list
    await fetchAds();
    await fetchUsageStats();
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleCreateAd = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const userPlan = user.plan || 'free';
    const planLimit = PLAN_LIMITS[userPlan];

    // Free plan cannot create ads
    if (userPlan === 'free' || planLimit.maxAds === 0) {
      setLimitReason('免費會員無法刊登廣告，請升級至 Basic 或 Premium 方案');
      setShowLimitModal(true);
      return;
    }

    // Check if user has reached their limit
    if (planLimit.maxAds !== null && usageStats.currentAds >= planLimit.maxAds) {
      setLimitReason(`您已達到 ${userPlan} 方案的廣告數量上限 (${planLimit.maxAds} 則)，請升級至 Premium 方案以刊登更多廣告`);
      setShowLimitModal(true);
      return;
    }

    // Open modal to create ad
    setEditingAdId(null);
    setShowEditModal(true);
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      '維修': '汽車維修',
      '保養': '汽車保養',
      '美容': '汽車美容',
      '改裝': '汽車改裝',
    };
    return labels[category] || category;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '未知';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '未知';
    if (date.getFullYear() === 1970) return '未知';
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="my-ads-list">
        <div className="my-ads-list__loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="my-ads-list">
      <div className="my-ads-list__header">
        <div className="my-ads-list__title-section">
          <h3 className="my-ads-list__title">我的廣告</h3>
          <span className="my-ads-list__count">{ads.length} 個廣告</span>
        </div>
        {ads.length > 0 && (
          <button
            onClick={handleCreateAd}
            className="my-ads-list__create-btn my-ads-list__create-btn--small"
          >
            + 刊登廣告
          </button>
        )}
      </div>

      {error && (
        <div className="my-ads-list__error">{error}</div>
      )}

      {ads.length === 0 ? (
        <div className="my-ads-list__empty">
          <p>您還沒有發布任何廣告</p>
          <button
            onClick={handleCreateAd}
            className="my-ads-list__create-btn"
          >
            發布新廣告
          </button>
        </div>
      ) : (
        <div className="my-ads-list__items">
          {ads.map((ad) => {
            const statusConfig = getStatusConfig(ad.status);
            const isRejected = ad.status === 'rejected';
            const isPending = ad.status === 'pending';

            return (
              <div key={ad.id} className={`my-ads-list__item my-ads-list__item--${ad.status}`}>
                <div className="my-ads-list__item-content">
                  <div className="my-ads-list__item-header">
                    <h4 className="my-ads-list__item-title">{ad.title}</h4>
                    <span className={`my-ads-list__status my-ads-list__status--${ad.status}`}>
                      <span className="my-ads-list__status-icon">{statusConfig.icon}</span>
                      <span className="my-ads-list__status-label">{statusConfig.label}</span>
                    </span>
                  </div>

                  {/* Status message for rejected/pending ads */}
                  {(isRejected || isPending) && (
                    <div className={`my-ads-list__status-message my-ads-list__status-message--${ad.status}`}>
                      {statusConfig.message}
                    </div>
                  )}

                  <div className="my-ads-list__item-meta">
                    <span>{getCategoryLabel(ad.category)}</span>
                    <span>•</span>
                    <span>{ad.region}</span>
                    {ad.district && <span> • {ad.district}</span>}
                  </div>

                  <div className="my-ads-list__item-stats">
                    <span>瀏覽 {ad.viewCount}</span>
                    <span>•</span>
                    <span>點擊 {ad.clickCount || 0}</span>
                    <span>•</span>
                    <span>{formatDate(ad.updatedAt)}</span>
                  </div>
                </div>

                <div className="my-ads-list__item-actions">
                  {isRejected ? (
                    <button
                      onClick={() => handleEdit(ad.id)}
                      className="my-ads-list__action-btn my-ads-list__action-btn--edit"
                    >
                      ✏️ 編輯重送
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEdit(ad.id)}
                      className="my-ads-list__action-btn my-ads-list__action-btn--edit"
                    >
                      編輯
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(ad.id, ad.title)}
                    disabled={deletingId === ad.id}
                    className="my-ads-list__action-btn my-ads-list__action-btn--delete"
                  >
                    {deletingId === ad.id ? '刪除中...' : '刪除'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AdForm Modal (Desktop) */}
      <AdFormModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        adId={editingAdId}
        onSuccess={handleEditSuccess}
      />

      {/* Limit Reached Modal */}
      <Modal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        size="small"
        title="已達到廣告數量上限"
      >
        <div className="my-ads-list__limit-modal">
          <p className="my-ads-list__limit-message">{limitReason}</p>
          <div className="my-ads-list__limit-actions">
            <button
              onClick={() => setShowLimitModal(false)}
              className="my-ads-list__limit-btn my-ads-list__limit-btn--cancel"
            >
              稍後再說
            </button>
            <button
              onClick={() => {
                setShowLimitModal(false);
                navigate('/pricing');
              }}
              className="my-ads-list__limit-btn my-ads-list__limit-btn--upgrade"
            >
              ⭐ 升級會員
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MyAdsList;
